import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import { Toaster } from 'sonner';

// Set axios defaults for all requests
axios.defaults.withCredentials = true;

// Add axios request interceptor to include auth token from localStorage
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) {
    // Always set the Authorization header if we have a token
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Add axios response interceptor to handle auth errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't redirect on 401 for auth/me endpoint (handled by AuthContext)
    const isAuthMeRequest = error.config?.url?.includes('/auth/me');
    const isAuthEndpoint = error.config?.url?.includes('/auth/');
    
    if (error.response?.status === 401 && !isAuthMeRequest && !isAuthEndpoint) {
      // Token is invalid or expired - log for debugging
      console.warn('401 Unauthorized - Token may be invalid or expired');
      console.warn('Failed URL:', error.config?.url);
      
      // Clear invalid token from storage
      localStorage.removeItem('session_token');
      document.cookie = 'session_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=None; Secure';
      delete axios.defaults.headers.common['Authorization'];
    }
    return Promise.reject(error);
  }
);

// Context
import { WorkspaceProvider } from '@/context/WorkspaceContext';
import { AuthProvider } from '@/context/AuthContext';

// Components
import CreditEnforcementOverlay from '@/components/CreditEnforcementOverlay';
import QATestingBanner from '@/components/QATestingBanner';
import SidebarLayout from '@/components/SidebarLayout';

// Pages
import LandingPage from '@/pages/LandingPage';
import GetStartedPage from '@/pages/GetStartedPage';
import BriefingPage from '@/pages/BriefingPage';
import ThemeSelectionPage from '@/pages/ThemeSelectionPage';
import DesignPreviewPage from '@/pages/DesignPreviewPage';
import DashboardPage from '@/pages/DashboardPage';
import AuthCallback from '@/pages/AuthCallback';
import SharedDesignPage from '@/pages/SharedDesignPage';
import JoinWorkspacePage from '@/pages/JoinWorkspacePage';
import AdminSupportPage from '@/pages/AdminSupportPage';
import AdminProjectsPage from '@/pages/AdminProjectsPage';
import PlaceOrderPage from '@/pages/PlaceOrderPage';
import CustomerPaymentPage from '@/pages/CustomerPaymentPage';
import CreditsPage from '@/pages/CreditsPage';
import TeamPage from '@/pages/TeamPage';
import SettingsPage from '@/pages/SettingsPage';
import SupportPage from '@/pages/SupportPage';
import FeatureRequestsPage from '@/pages/FeatureRequestsPage';
import ProtectedRoute from '@/components/ProtectedRoute';

// QA Testing Banner wrapper component
function QATestingWrapper() {
  const [qaSession, setQaSession] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Initialize QA session from localStorage on mount only
  const [initialized, setInitialized] = useState(false);
  
  if (!initialized) {
    const session = localStorage.getItem('qa_session');
    if (session) {
      try {
        const parsed = JSON.parse(session);
        if (parsed && !qaSession) {
          setQaSession(parsed);
        }
      } catch (e) {
        localStorage.removeItem('qa_session');
      }
    }
    setInitialized(true);
  }
  
  const handleEndSession = () => {
    localStorage.removeItem('qa_session');
    setQaSession(null);
    navigate('/admin/projects');
  };
  
  // Don't show on admin pages or landing
  const hideOnPaths = ['/', '/admin', '/get-started', '/shared', '/join', '/pay'];
  const shouldHide = hideOnPaths.some(p => location.pathname === p || location.pathname.startsWith(p + '/'));
  
  if (!qaSession || shouldHide) return null;
  
  return <QATestingBanner session={qaSession} onEndSession={handleEndSession} />;
}

function AppRouter() {
  const location = useLocation();
  
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check for session_id in URL fragment BEFORE routing
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  // Pages that should use the sidebar layout
  const sidebarRoutes = ['/dashboard', '/briefing', '/themes', '/preview', '/admin', '/place-order', '/feature-requests', '/support', '/settings', '/credits', '/team'];
  const shouldUseSidebar = sidebarRoutes.some(route => location.pathname.startsWith(route));
  
  const routes = (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/get-started" element={<GetStartedPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/briefing" element={<ProtectedRoute><BriefingPage /></ProtectedRoute>} />
      <Route path="/themes/:projectId" element={<ProtectedRoute><ThemeSelectionPage /></ProtectedRoute>} />
      <Route path="/preview/:projectId" element={<ProtectedRoute><DesignPreviewPage /></ProtectedRoute>} />
      <Route path="/place-order/:projectId" element={<ProtectedRoute><PlaceOrderPage /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/credits" element={<ProtectedRoute><CreditsPage /></ProtectedRoute>} />
      <Route path="/team" element={<ProtectedRoute><TeamPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
      <Route path="/feature-requests" element={<ProtectedRoute><FeatureRequestsPage /></ProtectedRoute>} />
      <Route path="/admin/support" element={<ProtectedRoute><AdminSupportPage /></ProtectedRoute>} />
      <Route path="/admin/projects" element={<ProtectedRoute><AdminProjectsPage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminProjectsPage /></ProtectedRoute>} />
      {/* Public routes - no authentication required */}
      <Route path="/shared/:shareToken" element={<SharedDesignPage />} />
      <Route path="/join/:inviteToken" element={<JoinWorkspacePage />} />
      <Route path="/pay/:paymentToken" element={<CustomerPaymentPage />} />
    </Routes>
  );
  
  // Wrap with SidebarLayout for authenticated pages
  if (shouldUseSidebar) {
    return <SidebarLayout>{routes}</SidebarLayout>;
  }
  
  return routes;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <WorkspaceProvider>
            <QATestingWrapper />
            <AppRouter />
            <CreditEnforcementOverlay />
            <Toaster position="top-right" richColors />
          </WorkspaceProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;